import { AXNUIContent } from "@iddataweb/idw-common-ui/types";
import {
    AxnAttributeTypeField,
    AxnAvailablePropertyCategory,
    AxnCompany,
    AxnIdentityProvider,
    AxnRelyingParty,
    AxnServiceOfferingDataSource
} from "@idw-types/data/reference-data/axn";
import {
    ApAssertion,
    Assertion,
    AttributeClassification,
    AttributeLabel,
    AttributeType,
    DeployedPolicyComponent,
    FallbackServiceMapping,
    RpServiceAttributeProperty,
    RpServiceAttributePropertyMap,
    ServiceOffering
} from "@idw-types/data/reference-data/generic";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationIdentityProvider,
    FormattedRpServiceConfigurationPolicy,
    FormattedRpServiceConfigurationPolicyConfig,
    RpServiceConfigurationAttribute,
    RpServiceConfigurationPolicyProperty,
    RpServiceConfigurationProperty,
    RpServiceConfigurationRedirect,
    RpServiceConfigurationServiceOffering,
    RpServiceConfigurationServiceOfferingAssertion
} from "@idw-types/data/rp-service-configuration";
import { FormattedWorkflowTemplateDetailsTemplateWorkflow } from "@idw-types/data/workflow-template-details";
import {
    FormattedWorkflowTemplateDetailsTemplate,
    WorkflowTemplateDetailsTemplate
} from "@idw-types/data/workflow-template-details/workflow-template-details-template";
import { extractAvailablePropertiesFromCategories } from "@utils/available-properties-util";
import { isNullOrUndefined } from "@utils/object-util";
import { formatAppliedProfiles } from "../applied-profiles/applied-profiles-formatter";
import { formatRpServiceConfigurationAttributes } from "../rp-services/rp-service-configuration-attribute-formatter";
import { formatRpServiceConfigurationProperties } from "../rp-services/rp-service-configuration-property-formatter";
import { formatRpServiceConfigurationServiceOfferings } from "../rp-services/rp-service-configuration-service-offering-formatter";
import { convertWorkflowTemplateDetailsPolicyConfigToFormattedRpServiceConfiguartionPolicyConfig } from "./workflow-template-details-policy-config-formatter";

export const formatWorkflowTemplateDetailsTemplate = (
    template: string,
    axnIdentityProviders: Array<AxnIdentityProvider>,
    axnRelyingParties: Array<AxnRelyingParty>,
    axnCompanies: Array<AxnCompany>,
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>,
    attributeTypes: Array<AttributeType>,
    attributeLabels: Array<AttributeLabel>,
    attributeClassifications: Array<AttributeClassification>,
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>,
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>,
    axnuiContent: AXNUIContent,
    deployedPolicyComponents: Array<DeployedPolicyComponent>,
    axnAttributeTypeFields: Array<AxnAttributeTypeField>,
    serviceOfferings: Array<ServiceOffering>,
    apAssertions: Array<ApAssertion>,
    assertions: Array<Assertion>,
    serviceOfferingDataSources: Array<AxnServiceOfferingDataSource>,
    fallbackServiceMappings: Array<FallbackServiceMapping>
): FormattedWorkflowTemplateDetailsTemplate => {
    let parsedTemplate: Partial<WorkflowTemplateDetailsTemplate> = {};
    try {
        parsedTemplate = JSON.parse(template);
    } catch (error) {
        console.log(`Error parsing workflow template details template: ${error}`);
    }

    const availableProperties = extractAvailablePropertiesFromCategories(axnAvailablePropertyCategories);

    const company = axnCompanies.find(c => c.id === `${parsedTemplate.workflow?.companyID}`);
    const relyingParty = axnRelyingParties.find(rp => rp.id === `${parsedTemplate.workflow?.relyingPartyID}`);
    const organization = axnCompanies.find(axnCompany => axnCompany.id === `${parsedTemplate?.workflow?.companyID}`) || null;
    const appliedProfiles = formatAppliedProfiles(
        (parsedTemplate.appliedProfiles || []),
        axnCompanies,
        availableProperties,
        true // shouldPopulateMissingProperties
    );

    const autoSubmitFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "autoSubmitFlag");
    const doNotSaveToPDSAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "doNotSaveToPDS");
    const ignorePDSDataAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "ignorePDSData");
    const isAsyncAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "isAsync");
    const optionalFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "optionalFlag");
    const placeholderFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "placeholderFlag");
    const requiredForSelfServiceVerificationAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "requiredForSelfServiceVerification");
    const userEditableFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "userEditableFlag");
    const userVisibleFlagAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "userVisibleFlag");
    const customToolTipAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "customToolTip");
    const displayFullValueAttributeProperty = rpServiceAttributeProperties.find(ap => ap.name === "displayFullValue");


    const rpServices: Array<FormattedRpServiceConfiguration> = (parsedTemplate.rpServices || []).map(service => {
        const rpServiceConfigProperties: Array<RpServiceConfigurationProperty> = service.properties.map(property => ({
            id: `${property.id}`,
            propertyID: `${property.propertyID}`,
            rpServiceID: `${property.rpServiceID}`,
            value: property.value
        }));

        const rpServiceConfigAttributes: Array<RpServiceConfigurationAttribute> = service.attributes.map(attr => ({
            attributeLabelID: `${attr.attributeLabelID}`,
            attributeTypeID: `${attr.attributeTypeID}`,
            autoSubmitFlag: `${attr.autoSubmitFlag}`,
            customError: attr.customError,
            customPINContactURL: attr.customPINContactURL,
            customTemplateName: attr.customTemplateName,
            customToolTip: attr.customToolTip,
            dependentAttributeID: `${attr.dependentAttributeID}`,
            doNotSaveToPDS: `${attr.doNotSaveToPDS}`,
            id: `${attr.id}`,
            ignorePDSData: `${attr.ignorePDSData}`,
            isAsync: `${attr.isAsync}`,
            optionalFlag: `${attr.optionalFlag}`,
            order: `${attr.order}`,
            placeholderFlag: `${attr.placeholderFlag}`,
            populateCredentialFromThisAttribute: `${attr.populateFromAcqAttrs}`,
            requiredForSelfServiceVerification: `${attr.requiredForSelfServiceVerification}`,
            userEditableFlag: `${attr.userEditableFlag}`,
            userVisibleFlag: `${attr.userVisibleFlag}`,
            displayFullValue: `${attr.displayFullValue}`,
            rpServiceID: `${attr.rpServiceID}`,
            status: attr.status
        }));

        const rpServiceConfigServiceOfferings: Array<RpServiceConfigurationServiceOffering> = service.serviceOfferings.map(so => {
            const assertions: Array<RpServiceConfigurationServiceOfferingAssertion> = so.assertions.map(assertion => ({
                apAssertionID: assertion.apAssertionID ? `${assertion.apAssertionID}` : null,
                assertionMessageID: assertion.assertionMessageID ? `${assertion.assertionMessageID}` : null,
                closeAssertionMessageID: assertion.closeAssertionMessageID ? `${assertion.closeAssertionMessageID}` : null,
                failAssertionMessageID: assertion.failAssertionMessageID ? `${assertion.failAssertionMessageID}` : null,
                id: assertion.id ? `${assertion.id}` : null,
                partialAssertionMessageID: assertion.partialAssertionMessageID ? `${assertion.partialAssertionMessageID}` : null,
                passAssertionMessageID: assertion.passAssertionMessageID ? `${assertion.passAssertionMessageID}` : null,
                serviceOfferingID: assertion.serviceOfferingID ? `${assertion.serviceOfferingID}` : null,
                status: assertion.status,
                unverifiedAssertionMessageID: assertion.unverifiedAssertionMessageID ? `${assertion.unverifiedAssertionMessageID}` : null,
                warnAssertionMessageID: assertion.warnAssertionMessageID ? `${assertion.warnAssertionMessageID}` : null
            }));

            const finalServiceOffering: RpServiceConfigurationServiceOffering = {
                assertions,
                category: null,
                dataSource: so.dataSource,
                fallbackRPServiceSOID: so.fallbackRPServiceSOID ? `${so.fallbackRPServiceSOID}` : null,
                fallbackServiceOfferingID: so.fallbackServiceOfferingID ? `${so.fallbackServiceOfferingID}` : null,
                fallbackStatus: so.fallbackStatus,
                id: so.id ? `${so.id}` : null,
                order: so.order ? `${so.order}` : null,
                processingType: so.processingType,
                processingTypeID: null,
                referenceCode: null,
                refreshRate: so.refreshRate,
                rpServiceID: `${service.id}`,
                serviceOfferingID: `${so.serviceOfferingID}`,
                status: so.status,
                weight: isNullOrUndefined(so.weight) ? null : `${so.weight}`
            };

            return finalServiceOffering;
        });

        const redirects: Array<RpServiceConfigurationRedirect> = service.redirects.map(redirect => ({
            id: `${redirect.id}`,
            redirectURI: redirect.redirectURI,
            rpServiceID: `${redirect.rpServiceID}`
        }));

        const idps: Array<FormattedRpServiceConfigurationIdentityProvider> = axnIdentityProviders.map(idp => {
            const foundIdp = (service.idps || []).find(selectedIdp => `${selectedIdp.identityProviderID}` === idp.id);
            return {
                identityProvider: idp,
                id: foundIdp?.id ? `${foundIdp.id}` : null,
                rpServiceID: foundIdp?.rpServiceID ? `${foundIdp.rpServiceID}` : null,
                addedToService: !!foundIdp
            }
        });
    

        const policyConfig = (parsedTemplate.policyConfigs || []).find(pc => pc.rpAXNServiceID === service.id);
        let formattedPolicyConfig: FormattedRpServiceConfigurationPolicyConfig = null;
        if (policyConfig) {
            formattedPolicyConfig = convertWorkflowTemplateDetailsPolicyConfigToFormattedRpServiceConfiguartionPolicyConfig(
                policyConfig,
                deployedPolicyComponents,
                axnAttributeTypeFields
            );
        }

        const selectedPolicies = (parsedTemplate.selectedPolicies || []).filter(sp => sp.rpAXNServiceID === service.id);

        const formattedPolicies: Array<FormattedRpServiceConfigurationPolicy> = [];
        selectedPolicies.forEach(sp => {
            const policy = (parsedTemplate.policies || []).find(p => p.id === sp.policyID);
            if (policy) {
                const spProperties: Array<RpServiceConfigurationPolicyProperty> = sp.properties.map(spProperty => ({
                    id: `${spProperty.id}`,
                    name: spProperty.name,
                    selectedPolicyID: `${spProperty.selectedPolicyID}`,
                    value: spProperty.value
                }));

                formattedPolicies.push({
                    apiKey: sp.apiKey,
                    id: `${sp.id}`,
                    policyID: `${policy.id}`,
                    properties: spProperties,
                    rpAXNServiceID: `${sp.rpAXNServiceID}`,
                    status: policy.status,
                    stepUpEnabled: (sp.properties || []).some(property => property.name === "stepUp.apiKey" && property.value)
                });
            }
        });

        const rpService: FormattedRpServiceConfiguration = {
            ...service,
            id: `${service.id}`,
            redirects,
            policies: formattedPolicies,
            policyConfig: formattedPolicyConfig,
            properties: formatRpServiceConfigurationProperties(
                rpServiceConfigProperties,
                availableProperties,
            ),
            attributes: formatRpServiceConfigurationAttributes(
                rpServiceConfigAttributes,
                attributeTypes,
                attributeLabels,
                attributeClassifications,
                rpServiceAttributePropertyMaps,
                autoSubmitFlagAttributeProperty,
                doNotSaveToPDSAttributeProperty,
                ignorePDSDataAttributeProperty,
                isAsyncAttributeProperty,
                optionalFlagAttributeProperty,
                placeholderFlagAttributeProperty,
                requiredForSelfServiceVerificationAttributeProperty,
                userEditableFlagAttributeProperty,
                userVisibleFlagAttributeProperty,
                customToolTipAttributeProperty,
                displayFullValueAttributeProperty,
                axnuiContent
            ),
            serviceOfferings: formatRpServiceConfigurationServiceOfferings(
                rpServiceConfigServiceOfferings,
                serviceOfferings,
                apAssertions,
                assertions,
                serviceOfferingDataSources,
                fallbackServiceMappings
            ),
            workflowID: parsedTemplate.workflow?.id ? `${parsedTemplate.workflow?.id}` : null,
            usePolicyManager: isNullOrUndefined(service.usePolicyManager) ? "false" : `${service.usePolicyManager}`,
            afterIDPAuthURL: service.afterIDPAuthURL,
            appliedProfiles: appliedProfiles,
            assertionScoreModifiers: service.assertionScoreModifiers,
            callBackURL: service.callBackURL,
            customIDPDisplayURL: service.customIDPDisplayURL,
            endpointTypeID: service.endpointTypeID ? `${service.endpointTypeID}` : null,
            errorURL: service.errorURL,
            generalSettings: {
                enableGatewayUI: true
            },
            idps: idps,
            loginHintSupported: false,
            permissions: {
                canArchive: true,
                canCancel: true,
                canDelete: true,
                canDeploy: true,
                canModify: true,
                canModifyAttributeProperties: true,
                canModifyBranding: true,
                canModifyCloseAssertionScoreModifiers: true,
                canModifyCloseTrustScoreModifiers: true,
                canModifyEndpointType: true,
                canModifyFailAssertionScoreModifiers: true,
                canModifyFailTrustScoreModifiers: true,
                canModifyNonRestrictedAttributeProperties: true,
                canModifyNonRestrictedProperties: true,
                canModifyPartialAssertionScoreModifiers: true,
                canModifyPartialTrustScoreModifiers: true,
                canModifyPassAssertionScoreModifiers: true,
                canModifyPassTrustScoreModifiers: true,
                canModifyServiceOfferings: true,
                canModifyUnverifiedAssertionScoreModifiers: true,
                canModifyUnverifiedTrustScoreModifiers: true,
                canModifyFallbackServiceOfferings: true,
                canViewFallbackServiceOfferings: true,
                canRestore: true,
                canReview: true,
                canSubmit: true,
                canView: true,
                canViewAttributeProperties: true,
                canViewBranding:  true,
                canViewCloseAssertionScoreModifiers: true,
                canViewCloseTrustScoreModifiers: true,
                canViewFailAssertionScoreModifiers: true,
                canViewFailTrustScoreModifiers: true,
                canViewNonRestrictedAttributeProperties: true,
                canViewNonRestrictedProperties: true,
                canViewPartialAssertionScoreModifiers: true,
                canViewPartialTrustScoreModifiers: true,
                canViewPassAssertionScoreModifiers: true,
                canViewPassTrustScoreModifiers: true,
                canViewServiceOfferings: true,
                canViewUnverifiedAssertionScoreModifiers: true,
                canViewUnverifiedTrustScoreModifiers: true,
                canModifyAPIKey: true,
                canModifyAXNUICustomization: true,
                canModifyDCUICustomization: true,
                canModifyRPServiceProperties: true,
                canViewAXNUICustomization: true,
                canViewDCUICustomization: true,
                canViewRPServiceProperties: true
            },
            relyingParty: relyingParty,
            scoreAttributeTypeIDs: [],
            scoreServiceOfferingsIDs: [],
            serviceType: service.serviceType,
            sharedSecret: service.sharedSecret,
            status: service.status,
            trustScoreModifiers: service.trustScoreModifiers,
            workflow: null,
            apiKey: service.apiKey,
            serviceName: service.serviceName,
            organization
        }

        return rpService;
    });

    const workflow: FormattedWorkflowTemplateDetailsTemplateWorkflow = {
        company: company,
        id: parsedTemplate.workflow?.id || null,
        name: parsedTemplate.workflow?.name || "",
        sku: parsedTemplate.workflow?.sku || null,
        relyingParty: relyingParty,
        rpServiceWorkflows: parsedTemplate.workflow?.rpServiceWorkflows || [],
        status: parsedTemplate.workflow?.status || null,
        workflowKey: parsedTemplate.workflow?.workflowKey || null,
        workflowVariants: parsedTemplate.workflow?.workflowVariants || []
    };

    return {
        appliedProfiles: appliedProfiles,
        rpServices: rpServices,
        workflow: workflow,
        policies: parsedTemplate.policies,
        policyConfigs: parsedTemplate.policyConfigs,
        selectedPolicies: parsedTemplate.selectedPolicies
    };
};

export const formatWorkflowTemplateDetailsTemplatePartial = (
    template: string,
    axnRelyingParties: Array<AxnRelyingParty>,
    axnCompanies: Array<AxnCompany>,
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>
): FormattedWorkflowTemplateDetailsTemplate => {
    let parsedTemplate: Partial<WorkflowTemplateDetailsTemplate> = {};
    try {
        parsedTemplate = JSON.parse(template);
    } catch (error) {
        console.log(`Error parsing workflow template details template: ${error}`);
    }

    const company = axnCompanies.find(c => c.id === `${parsedTemplate.workflow?.companyID}`);
    const relyingParty = axnRelyingParties.find(rp => rp.id === `${parsedTemplate.workflow?.relyingPartyID}`);

    const availableProperties = extractAvailablePropertiesFromCategories(axnAvailablePropertyCategories);

    const appliedProfiles = formatAppliedProfiles(
        (parsedTemplate.appliedProfiles || []),
        axnCompanies,
        availableProperties,
        true // shouldPopulateMissingProperties
    );

    const rpServices: Array<FormattedRpServiceConfiguration> = (parsedTemplate.rpServices || []).map(service => {
        const rpServiceConfigProperties: Array<RpServiceConfigurationProperty> = service.properties.map(property => ({
            id: `${property.id}`,
            propertyID: `${property.propertyID}`,
            rpServiceID: `${property.rpServiceID}`,
            value: property.value
        }));

        const redirects: Array<RpServiceConfigurationRedirect> = service.redirects.map(redirect => ({
            id: `${redirect.id}`,
            redirectURI: redirect.redirectURI,
            rpServiceID: `${redirect.rpServiceID}`
        }));

        const finalService: FormattedRpServiceConfiguration = {
            ...service,
            id: `${service.id}`,
            properties: formatRpServiceConfigurationProperties(
                rpServiceConfigProperties,
                availableProperties,
            ),
            redirects,
            attributes: [],
            serviceOfferings: [],
            afterIDPAuthURL: null,
            appliedProfiles: [],
            assertionScoreModifiers: [],
            callBackURL: null,
            customIDPDisplayURL: null,
            endpointTypeID: null,
            errorURL: null,
            generalSettings: null,
            idps: [],
            loginHintSupported: false,
            permissions: null,
            policies: [],
            policyConfig: null,
            relyingParty: null,
            organization: null,
            scoreAttributeTypeIDs: [],
            scoreServiceOfferingsIDs: [],
            serviceType: null,
            sharedSecret: null,
            status: null,
            trustScoreModifiers: [],
            workflow: null,
            workflowID: null,
            apiKey: null,
            serviceName: null,
            usePolicyManager: isNullOrUndefined(service.usePolicyManager) ? "false" : `${service.usePolicyManager}`
        };

        return finalService;
    });

    const workflow: FormattedWorkflowTemplateDetailsTemplateWorkflow = {
        company: company,
        id: parsedTemplate.workflow.id,
        name: parsedTemplate.workflow.name,
        sku: parsedTemplate.workflow.sku || null,
        relyingParty: relyingParty,
        rpServiceWorkflows: parsedTemplate.workflow.rpServiceWorkflows,
        status: parsedTemplate.workflow.status,
        workflowKey: parsedTemplate.workflow.workflowKey,
        workflowVariants: parsedTemplate.workflow.workflowVariants
    };

    return {
        appliedProfiles: appliedProfiles,
        rpServices: rpServices,
        workflow: workflow,
        policies: parsedTemplate.policies,
        policyConfigs: parsedTemplate.policyConfigs,
        selectedPolicies: parsedTemplate.selectedPolicies
    };
};